import { useCallback, useRef, useState } from 'react';
import { authAPI } from '../../services/api';

// 樂觀更新送訊息（比照 LINE）：按下送出當下訊息就先出現在畫面上（半透明＝傳送中），
// 成功→換成後端回傳的真訊息、失敗→氣泡旁出現紅色驚嘆號，點一下重送。
//
// 設計重點（刻意不動既有 messages 狀態的任何邏輯）：
// - 樂觀訊息「另存一個 pending 陣列」，不混進 messages。messages 既有的 id 去重、
//   `sort((a,b) => a.id - b.id)`（輪詢合併用）、WS append、載入更早訊息 prepend 等邏輯
//   全部原封不動——如果把 tmp-x 這種字串 id 混進去，那個數字排序會變成 NaN 比較。
// - 因此 tempId 用字串（`tmp-1`），永遠不可能跟後端的整數 id 撞號。
// - 成功後才呼叫 appendMessage(真訊息)，交由既有的 id 去重處理「WS 先到／POST 回應後到」
//   的競態（兩邊都是同一顆真 id，重複會被既有邏輯擋掉）。
//
// send() 永遠 resolve、不會 reject：
// - 文字訊息：Composer 不 await，按下就清空輸入框（這是「不要有 load 等待感」的重點）
// - 圖片訊息：Composer 仍逐張 await 以維持既有的送出順序保證（後端 last_message_* 不互相覆蓋），
//   所以這裡必須回傳一個「網路完成才 resolve」的 promise，而且不能 reject（否則迴圈會中斷）

// 產生 client_msg_id：用戶端在送出當下自己造一個唯一 id 隨訊息送出，後端存下來、
// 並在**所有回傳管道**（POST 回應 + WebSocket）原樣吐回來。這是業界標準作法
// （Matrix 叫 txn_id、Slack 叫 client_msg_id、XMPP 叫 origin-id、Telegram 叫 random_id）。
//
// 【為什麼不能直接叫 crypto.randomUUID()】它不是到處都有：
//   - 只在 secure context 掛得上去（用 http:// 開區網測試機時 window.crypto 本身就沒有它）
//   - Android WebView < Chrome 92、iOS WKWebView < 15.4 完全沒有這支
//   本專案打包成 Capacitor Hybrid App，家長手機上的 WebView 版本我們控制不了；
//   一旦這裡丟 TypeError，送出流程會在第一行就炸掉＝訊息完全送不出去，比原本的 bug 更嚴重。
// 所以退而求其次用 getRandomValues 自己組一個 v4，再不行才用時間戳＋Math.random。
// 最後那層的碰撞機率不是理論上的 0，但要出事得「同一對話、同一發送者、兩則都還在對帳窗口內」
// 同時撞號，實務上不會發生；而且退化到那層代表這台裝置連 crypto 都沒有，本來就是極端個案。
const newClientMsgId = () => {
    const c = typeof window !== 'undefined' ? window.crypto : undefined;
    if (c && typeof c.randomUUID === 'function') {
        // 有些 WebView 在非 secure context 下這支存在但呼叫會丟錯，所以仍要包起來
        try { return c.randomUUID(); } catch { /* 落到下一層 */ }
    }
    if (c && typeof c.getRandomValues === 'function') {
        const b = new Uint8Array(16);
        c.getRandomValues(b);
        b[6] = (b[6] & 0x0f) | 0x40;   // version 4
        b[8] = (b[8] & 0x3f) | 0x80;   // variant 10x
        const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
        return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
    return `t${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function useOptimisticSend({ convId, user, appendMessage, messages }) {
    const [pending, setPending] = useState([]);
    const seqRef = useRef(0);

    const roleOf = (u) => {
        const r = u?.role;
        if (r === 'parent') return 'parent';
        if (['receptionist', 'admin', 'super_admin'].includes(r)) return 'admin';
        return 'teacher';
    };

    // 實際打 API 並依結果更新該筆 pending 的狀態；成功則把它從 pending 移除、換成真訊息
    const dispatch = useCallback((tempId, body) => {
        return authAPI.chat.send(convId, body)
            .then((r) => {
                setPending((p) => p.filter((m) => m.id !== tempId));
                if (r?.data) appendMessage(r.data);
            })
            .catch(() => {
                setPending((p) => p.map((m) => (m.id === tempId ? { ...m, _status: 'failed' } : m)));
            });
    }, [convId, appendMessage]);

    const send = useCallback((body) => {
        seqRef.current += 1;
        const tempId = `tmp-${seqRef.current}`;
        const clientMsgId = newClientMsgId();
        // client_msg_id 一併送給後端：① 真訊息回來時前端據此精確認出樂觀副本（見 reconcile）
        // ② 後端據此做冪等——重送同一個 id 只會拿回既有那則，不會變成兩則真訊息。
        // 存進 _body 而不是每次重打時重新產生，正是為了讓 ② 成立（重送必須是同一個 id）。
        const sendBody = { ...body, client_msg_id: clientMsgId };
        const optimistic = {
            id: tempId,
            conversation_id: convId,
            sender_id: user?.id,
            sender_role: roleOf(user),
            sender_name: user?.full_name || null,
            content_type: body.content_type,
            text: body.text ?? null,
            image_url: body.image_url ?? null,
            created_at: new Date().toISOString(),
            _status: 'sending',
            _body: sendBody,        // 重送時原樣再打一次（含同一個 client_msg_id）
            _clientMsgId: clientMsgId,
        };
        setPending((p) => [...p, optimistic]);
        return dispatch(tempId, sendBody);
    }, [convId, user, dispatch]);

    // 【對帳】把「已經有對應真訊息」的樂觀副本挑掉。
    //
    // 為什麼需要對帳：真訊息會從 POST 回應與 WebSocket **兩條獨立管道**回來，哪條先到不一定。
    // WS 先到時，樂觀副本與真訊息會同時在畫面上（＝使用者看到重複），直到 POST 回應才消失。
    //
    // 【2026-08-17 改用 client_msg_id 精確比對】原本是拿「同發送者＋同型別＋同內容＋id 大於
    // 送出當下最大 id」去猜配對。那是 heuristic，猜錯就出事，實際踩過兩次：
    //   - 比對到「內容相同的舊訊息」→ 樂觀副本被濾掉、畫面什麼都沒出現＝「訊息傳不出去」
    //   - 為了補救而加的 _sinceId 分界線，只是把 heuristic 修得更複雜，沒有解決根因
    // 現在兩份東西有了共同身分（client_msg_id），配對是精確的：不必比內容、不必管時間、
    // 不必管哪條管道先到。連同 _sinceId 一起刪掉。
    //
    // 【為什麼 failed 也一起對帳】只要真訊息帶著我的 client_msg_id 出現，就證明這則**確實
    // 送成功了**，不管 HTTP 層回報什麼（POST 逾時但後端其實已經寫入、真訊息從 WS 進來，
    // 是很常見的情境）。舊的內容比對版本不敢這樣做——會把「真的送失敗、但內容剛好跟舊訊息
    // 相同」的那筆誤判成成功而讓紅色驚嘆號消失，使用者以為送出去了。有精確 id 就沒這個風險。
    //
    // 【向後相容：舊後端不回傳 client_msg_id 時】arrived 會是空集合，這裡原樣回傳、什麼都不濾，
    // 等於退回「以 POST 回應為準」的行為（＝這次改動之前、2026-08-13 之前的原始行為）：
    // WS 比 POST 早到的那段空窗期會短暫看到重複，POST 一回來就消失。
    // 刻意選「短暫重複」而不是退回內容比對：重複只是看起來怪、會自己在一秒內收斂；
    // 而內容比對猜錯是把訊息藏起來，使用者會以為沒送出去而再送一次，那才是真的製造出兩則訊息。
    const reconcile = useCallback((list) => {
        if (!list.length || !messages || !messages.length) return list;
        const arrived = new Set();
        for (const m of messages) {
            if (m.client_msg_id) arrived.add(m.client_msg_id);
        }
        if (!arrived.size) return list;
        const kept = list.filter((pm) => !arrived.has(pm._clientMsgId));
        return kept.length === list.length ? list : kept;
    }, [messages]);

    // 【為什麼要在渲染時就過濾 —— 2026-08-17 user 回報「訊息閃一下變兩個又變回一個」】
    // 上一版只在 useEffect 裡清 state。但 React 是**先渲染、才跑 effect**：
    //   ① 樂觀副本插入 → 1 顆
    //   ② WS 真訊息抵達、渲染 → **2 顆**  ← 使用者看到的那一閃
    //   ③ effect 才跑、移除樂觀副本、再渲染 → 1 顆
    // ②→③ 之間必然有一個影格兩者並存，無論多快都會閃。
    // 改成把「要顯示的樂觀副本」當成**衍生值**在渲染時算出來，那一格就不存在了。
    const visiblePending = reconcile(pending);

    // 不需要另外用 effect 清 state：dispatch 的 .then() 本來就會在 POST 回應時移除該筆
    // pending，所以 state 一定清得掉；上面的對帳只是為了補上「WS 先於 POST 抵達」那段
    // 空窗期的**顯示**。而且本 repo 的 react-hooks 規則把「在 effect 裡同步 setState」
    // 列為 error（會觸發連鎖渲染），衍生值本來就是更對的做法。

    // 點紅色驚嘆號 → 該筆退回「傳送中」再打一次。
    // _body 帶著原本那顆 client_msg_id，所以後端會走冪等：若第一次其實已經寫進 DB
    // （只是回應在路上掉了），重送只會拿回既有那則，不會多出第二則真訊息。
    const retry = useCallback((tempId) => {
        let body = null;
        setPending((p) => p.map((m) => {
            if (m.id !== tempId) return m;
            body = m._body;
            return { ...m, _status: 'sending' };
        }));
        if (body) dispatch(tempId, body);
    }, [dispatch]);

    const discard = useCallback((tempId) => {
        setPending((p) => p.filter((m) => m.id !== tempId));
    }, []);

    // 回傳過濾後的清單：呼叫端拿到的就是「該顯示的」，不必自己再對帳一次
    return { pending: visiblePending, send, retry, discard };
}
